import React, { useEffect, useMemo, useState } from 'react';
import { Plug, Blocks, CreditCard, Bot, type LucideIcon } from 'lucide-react';
import skillAccessService from '../../services/skill-access.service';
import { getSkillIcon } from '../../types/skill.types';
import type { AvailableSkill } from '../../types/skill-access.types';
import { useActiveOrgId } from '../../contexts/AuthContext';

interface AgentWelcomeProps {
  onTopicClick: (message: string) => void;
}

const ICON_MAP: Record<string, LucideIcon> = {
  Plug,
  Blocks,
  CreditCard,
  Bot,
};

interface QuickAction {
  key: string;
  icon: LucideIcon;
  label: string;
  message: string;
}

function pickMessage(skill: AvailableSkill): string {
  // `/skills/available` не отдаёт trigger_phrases — слать slash-команду, она запускает навык.
  return skill.command?.trim() || skill.name;
}

const AgentWelcome: React.FC<AgentWelcomeProps> = ({ onTopicClick }) => {
  // Whitelist /skills/available фильтруется по ролям в активной организации —
  // перезагружаем при смене орг.
  const currentOrgId = useActiveOrgId();

  const [skills, setSkills] = useState<AvailableSkill[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    skillAccessService
      .getAvailable() // RBAC-фильтрованный список доступных пользователю навыков
      .then((res) => {
        if (cancelled) return;
        setSkills(Array.isArray(res.skills) ? res.skills : []);
      })
      .catch(() => {
        if (cancelled) return;
        setSkills([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentOrgId]);

  const actions = useMemo<QuickAction[]>(() => {
    // Backend уже отфильтровал по доступу роли — клиентская фильтрация не нужна.
    // AvailableSkill не несёт display_order — сортируем по имени (стабильно, по алфавиту).
    return [...skills]
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      .slice(0, 3)
      .map((s) => ({
        key: s.code,
        icon: ICON_MAP[getSkillIcon(s.code)] ?? Bot,
        label: s.name,
        message: pickMessage(s),
      }));
  }, [skills]);

  return (
    <div className="flex flex-col-dir flex-1 gap-4" style={{ padding: 'var(--space-6) var(--space-4)' }}>
      <div>
        <h3 className="m-0 text-base font-semibold text-gray-900">
          AI Агент
        </h3>
        <p className="text-sm text-gray-600" style={{ margin: 'var(--space-1) 0 0', lineHeight: 'var(--leading-relaxed)' }}>
          Опишите задачу своими словами или выберите один из навыков ниже
        </p>
      </div>

      {(loading || actions.length > 0) && (
        <div className="flex flex-col-dir gap-2">
          <span className="text-xs text-gray-500 font-medium tracking-wide" style={{ textTransform: 'uppercase' as const }}>
            Быстрые действия
          </span>

          {loading &&
            [1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-lg bg-gray-100"
                style={{ height: 44, animation: 'shimmer 1.5s infinite' }}
                aria-hidden="true"
              />
            ))}

          {!loading &&
            actions.map(({ key, icon: Icon, label, message }) => (
              <button
                key={key}
                type="button"
                onClick={() => onTopicClick(message)}
                className="flex items-center gap-3 bg-gray-50 border-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700 w-full" style={{ padding: 'var(--space-3) var(--space-4)', transition: 'all 0.15s', textAlign: 'left' as const, fontFamily: 'inherit', minHeight: 44 }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--primary-50)';
                  e.currentTarget.style.borderColor = 'var(--primary-200)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'var(--gray-50)';
                  e.currentTarget.style.borderColor = 'var(--gray-200)';
                }}
              >
                <Icon size={16} className="flex-shrink-0 text-gray-500" />
                {label}
              </button>
            ))}
        </div>
      )}

      <p className="text-xs text-gray-500 m-0">
        Или опишите задачу своими словами ↓
      </p>
    </div>
  );
};

export default AgentWelcome;
